import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, User, Buildings, Sparkle, ArrowRight, Globe } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { PLATFORMS } from "../lib/meta";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

export default function Onboarding() {
  const { user, clients, loadClients, setActiveClientId, refreshOrg } = useApp();
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState("own");
  const [form, setForm] = useState({ name: "", industry: "", website: "" });
  const [clientId, setClientId] = useState(null);
  const [platforms, setPlatforms] = useState(["linkedin", "x"]);
  const [brief, setBrief] = useState("");
  const [variants, setVariants] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (step === 0 && clients?.length > 0) navigate("/dashboard", { replace: true });
  }, [step, clients, navigate]);

  const pick = (k) => {
    setKind(k);
    setForm({ ...form, name: k === "own" ? `${user?.name?.split(" ")[0] || "My"}'s Brand` : "" });
    setPlatforms(k === "own" ? ["linkedin", "x"] : ["instagram", "linkedin"]);
    setStep(1);
  };

  const createBrand = async () => {
    if (!form.name.trim()) return toast.error("Give the brand a name");
    setBusy(true);
    try {
      const { data } = await api.post("/clients", form);
      setClientId(data.client_id);
      setActiveClientId(data.client_id);
      await loadClients();
      if (form.website.trim()) {
        toast.info("Reading the website to learn the voice… (1 credit)");
        try {
          const { data: learned } = await api.post("/ai/learn-brand", { client_id: data.client_id, url: form.website.trim() });
          await api.put(`/clients/${data.client_id}/brand`, { ...learned.suggestions, brand_colors: [], banned_words: [], languages: ["en"], target_cadence: {} });
          toast.success("Voice learned from the website");
        } catch {
          toast.warning("Couldn't read the site — you can fill Brand Intelligence later");
        }
      }
      refreshOrg();
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the brand");
    } finally { setBusy(false); }
  };

  const generate = async () => {
    if (!brief.trim()) return toast.error("Tell it what to post about");
    if (!platforms.length) return toast.error("Pick at least one platform");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate", { client_id: clientId, platforms, brief });
      setVariants(data.variants);
      refreshOrg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await Promise.all(platforms.filter((p) => variants?.[p]?.caption).map((p) =>
        api.post("/drafts", { client_id: clientId, platform: p, caption: variants[p].caption, ai_generated: true })));
      toast.success("First drafts saved — welcome to your studio 🎉");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the drafts — try again");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[hsl(30,33%,98%)] flex flex-col items-center px-4 py-10" data-testid="onboarding-page">
      <div className="flex items-center gap-2 mb-8">
        <span className="w-7 h-7 rounded-lg bg-[hsl(9,69%,62%)] flex items-center justify-center"><Lightning size={15} weight="fill" color="#fff" /></span>
        <span className="font-display font-bold text-lg tracking-tight">Celebre</span>
      </div>
      <div className="flex gap-2 mb-8">
        {[0, 1, 2].map((i) => <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i <= step ? "w-8 bg-[hsl(9,69%,62%)]" : "w-4 bg-slate-200"}`} />)}
      </div>

      <div className="w-full max-w-lg fade-up">
        {step === 0 && (
          <div className="text-center">
            <h1 className="font-display font-bold text-3xl tracking-tight">Who are we posting for?</h1>
            <p className="text-sm text-slate-500 mt-2">Two minutes from now you'll have your first drafted post.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <button data-testid="onboard-own-brand" onClick={() => pick("own")}
                className="bg-white border-2 border-slate-200 hover:border-[hsl(9,69%,62%)] rounded-2xl p-7 text-left hover-lift transition-colors">
                <User size={26} weight="duotone" className="text-[hsl(9,69%,62%)]" />
                <p className="font-display font-bold text-lg mt-3">My own brand</p>
                <p className="text-[13px] text-slate-500 mt-1">Founder, consultant, creator — you're the client.</p>
              </button>
              <button data-testid="onboard-client-brand" onClick={() => pick("client")}
                className="bg-white border-2 border-slate-200 hover:border-[hsl(9,69%,62%)] rounded-2xl p-7 text-left hover-lift transition-colors">
                <Buildings size={26} weight="duotone" className="text-[hsl(9,69%,62%)]" />
                <p className="font-display font-bold text-lg mt-3">A client's brand</p>
                <p className="text-[13px] text-slate-500 mt-1">Agency mode — add the first of many.</p>
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="bg-white border border-slate-200/80 rounded-2xl p-8 card-float">
            <h1 className="font-display font-bold text-2xl tracking-tight">{kind === "own" ? "Set yourself up as the client" : "Add the client brand"}</h1>
            <p className="text-sm text-slate-500 mt-1">{kind === "own" ? "Your name, your niche — treated with agency rigor." : "Just the basics; the Brand Intelligence grows later."}</p>
            <div className="mt-6 space-y-3">
              <Input data-testid="onboard-name-input" placeholder={kind === "own" ? "Brand name (e.g. Jane Doe)" : "Client / brand name"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="onboard-industry-input" placeholder={kind === "own" ? "Your niche (e.g. fintech consulting)" : "Industry"} value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
              <div>
                <Input data-testid="onboard-website-input" placeholder="Website (optional)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                {form.website.trim() && <p className="mt-1.5 text-[12px] text-[hsl(9,55%,48%)] flex items-center gap-1.5"><Globe size={12} /> We'll read it and learn the voice automatically (1 credit)</p>}
              </div>
              <Button data-testid="onboard-create-button" onClick={createBrand} disabled={busy} className="w-full rounded-lg bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)] gap-2">
                {busy ? "Setting up…" : "Continue"} <ArrowRight size={15} />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white border border-slate-200/80 rounded-2xl p-8 card-float">
            <h1 className="font-display font-bold text-2xl tracking-tight">Your first post</h1>
            <p className="text-sm text-slate-500 mt-1">One brief. The co-pilot drafts it per platform — you stay the editor.</p>
            <div className="mt-6 space-y-4">
              <div className="flex flex-wrap gap-2">
                {Object.entries(PLATFORMS).map(([k, p]) => (
                  <button key={k} data-testid={`onboard-platform-${k}`} onClick={() => setPlatforms((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k])}
                    className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${platforms.includes(k) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
              <Textarea data-testid="onboard-brief-input" rows={3} value={brief} onChange={(e) => setBrief(e.target.value)}
                placeholder={kind === "own" ? `e.g. "Introduce myself and what I help ${form.industry || "clients"} with"` : `e.g. "Announce our spring launch"`} />
              {!variants ? (
                <Button data-testid="onboard-generate-button" onClick={generate} disabled={busy} className="w-full rounded-lg bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)] gap-2">
                  <Sparkle size={15} weight="fill" /> {busy ? "Drafting…" : "Draft my first post (1 credit)"}
                </Button>
              ) : (
                <>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {platforms.map((p) => variants[p]?.caption && (
                      <div key={p} className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{PLATFORMS[p].label}</p>
                        <p className="mt-1 text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed">{variants[p].caption}</p>
                      </div>
                    ))}
                  </div>
                  <Button data-testid="onboard-finish-button" onClick={finish} disabled={busy} className="w-full rounded-lg bg-slate-900 hover:bg-slate-700 gap-2">
                    Save drafts & open my studio <ArrowRight size={15} />
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        <button data-testid="onboard-skip-link" onClick={() => navigate("/dashboard")} className="mt-6 mx-auto block text-[13px] text-slate-400 hover:text-slate-700 transition-colors">
          Skip — I'll explore on my own
        </button>
      </div>
    </div>
  );
}
